/** Códigos PLAME de gratificación (iguales a liquidaciones). */
export const CODIGO_PLAME_GRATIFICACION = '0406';
/** Bonificación extraordinaria proporcional Ley (código operativo en liquidaciones). */
export const CODIGO_PLAME_BONIF_EXTRAORDINARIA = '0313';
/** Variante legacy / catálogo cercano (aparece en algunas planillas). */
export const CODIGO_PLAME_BONIF_EXTRAORDINARIA_ALT = '0312';

export const CODIGOS_GRATIFICACION_PLAME = [
  CODIGO_PLAME_GRATIFICACION,
  CODIGO_PLAME_BONIF_EXTRAORDINARIA,
  CODIGO_PLAME_BONIF_EXTRAORDINARIA_ALT,
];

/**
 * Concepto de liquidación en planilla (sí va en boleta ordinaria).
 */
export function esConceptoLiquidacionPlanilla(concepto) {
  if (!concepto) return false;
  const origen = concepto.origenCalculo || concepto.origen_calculo;
  return origen === 'liquidacion';
}

/**
 * Declaración de viáticos (tareo / viáticos standard): se informa en planilla/REM
 * pero no afecta el neto a pagar / boleta ordinaria.
 * Código habitual PLAME: 0917 CONDICIONES DE TRABAJO.
 */
export function esConceptoDeclaracionViatico(concepto) {
  if (!concepto) return false;
  if (esConceptoLiquidacionPlanilla(concepto)) return false;
  const origen = String(concepto.origenCalculo || concepto.origen_calculo || concepto.origen || '');
  if (origen === 'viatico' || origen === 'viatico_declaracion' || origen.includes('viatico')) {
    return true;
  }
  const codigo = String(
    concepto.codigoPlame
      || concepto.codigo
      || concepto.codigo_plame
      || concepto.plame_configuracion?.codigo_plame
      || ''
  );
  // 0917 = CONDICIONES DE TRABAJO (viáticos standard)
  if (codigo === '0917') return true;
  const desc = String(
    concepto.descripcion
      || concepto.concepto
      || concepto.nombre
      || concepto.plame_configuracion?.descripcion
      || ''
  )
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  if (desc.includes('condiciones de trabajo')) return true;
  return false;
}

/**
 * Declaración de gratificaciones / PLAME (solo si viene del módulo de gratificaciones).
 * Criterio: origen_calculo = gratificacion, o flag excluir (sin ser viático/liquidación).
 */
export function esConceptoDeclaracionGratificacion(concepto) {
  if (!concepto) return false;
  if (esConceptoLiquidacionPlanilla(concepto)) return false;
  if (esConceptoDeclaracionViatico(concepto)) return false;
  const origen = concepto.origenCalculo || concepto.origen_calculo;
  if (origen === 'gratificacion' || origen === 'gratificacion_declaracion') return true;
  if (concepto.excluirBoletaOrdinaria || concepto.excluir_boleta_ordinaria) return true;
  return false;
}

/** Ingreso informado en planilla/REM que no forma parte del neto a pagar. */
export function esConceptoSoloDeclaracion(concepto) {
  if (!concepto || esConceptoLiquidacionPlanilla(concepto)) return false;
  if (concepto.excluirBoletaOrdinaria || concepto.excluir_boleta_ordinaria) return true;
  return esConceptoDeclaracionGratificacion(concepto) || esConceptoDeclaracionViatico(concepto);
}

export function filtrarConceptosBoletaOrdinaria(conceptos = []) {
  return (conceptos || []).filter((c) => !esConceptoSoloDeclaracion(c));
}

export function separarIngresosPlanillaYDeclaracion(ingresos = []) {
  const lista = ingresos || [];
  const planilla = [];
  const declaracionGrati = [];
  const declaracionViatico = [];
  lista.forEach((c) => {
    if (esConceptoDeclaracionViatico(c)) {
      declaracionViatico.push(c);
    } else if (esConceptoSoloDeclaracion(c)) {
      // Grati (0406/0313) u otro ingreso solo-declaración
      declaracionGrati.push(c);
    } else {
      planilla.push(c);
    }
  });
  const sum = (arr) => arr.reduce((s, c) => s + (parseFloat(c.monto) || 0), 0);
  const declaracion = [...declaracionGrati, ...declaracionViatico];
  return {
    planilla,
    declaracion,
    declaracionGrati,
    declaracionViatico,
    totalPlanilla: sum(planilla),
    totalDeclaracion: sum(declaracion),
    totalDeclaracionGrati: sum(declaracionGrati),
    totalDeclaracionViatico: sum(declaracionViatico),
  };
}

/**
 * Empleado listo para boleta ordinaria: sin conceptos solo-declaración
 * (gratificaciones / viáticos) y totales recalculados sin esos montos.
 * Conceptos 0406/0313 cargados manualmente se conservan.
 */
export function empleadoParaBoletaOrdinaria(empleado) {
  if (!empleado) return empleado;
  const ingresos = filtrarConceptosBoletaOrdinaria(empleado.conceptos?.ingresos || []);
  const descuentos = empleado.conceptos?.descuentos || [];
  const aportes = empleado.conceptos?.aportes || [];
  const totalIngresos = ingresos.reduce((s, c) => s + (parseFloat(c.monto) || 0), 0);
  const totalDescuentos = descuentos.reduce((s, c) => s + (parseFloat(c.monto) || 0), 0);
  const totalAportes = aportes.reduce((s, c) => s + (parseFloat(c.monto) || 0), 0);

  const next = {
    ...empleado,
    conceptos: {
      ...empleado.conceptos,
      ingresos,
      descuentos,
      aportes,
    },
    totales: {
      ...(empleado.totales || {}),
      totalIngresos,
      totalDescuentos,
      totalAportes,
      netoAPagar: totalIngresos - totalDescuentos,
    },
  };

  // Misma exclusión en boleta interna (viáticos/grati con destino interna)
  if (empleado.conceptosInternos || empleado.totalesInternos) {
    const ingresosInt = filtrarConceptosBoletaOrdinaria(empleado.conceptosInternos?.ingresos || []);
    const descuentosInt = empleado.conceptosInternos?.descuentos || [];
    const aportesInt = empleado.conceptosInternos?.aportes || [];
    const ti = ingresosInt.reduce((s, c) => s + (parseFloat(c.monto) || 0), 0);
    const td = descuentosInt.reduce((s, c) => s + (parseFloat(c.monto) || 0), 0);
    const ta = aportesInt.reduce((s, c) => s + (parseFloat(c.monto) || 0), 0);
    next.conceptosInternos = {
      ...(empleado.conceptosInternos || {}),
      ingresos: ingresosInt,
      descuentos: descuentosInt,
      aportes: aportesInt,
    };
    next.totalesInternos = {
      ...(empleado.totalesInternos || {}),
      totalIngresos: ti,
      totalDescuentos: td,
      totalAportes: ta,
      netoAPagar: ti - td,
    };
  }

  return next;
}

export function conceptosSoloDeclaracionGratificacion(empleado, boleta = 'ambas') {
  const plame = (empleado?.conceptos?.ingresos || []).filter(esConceptoDeclaracionGratificacion);
  const interna = (empleado?.conceptosInternos?.ingresos || []).filter(esConceptoDeclaracionGratificacion);
  if (boleta === 'plame') return plame;
  if (boleta === 'interna') return interna;
  return [...plame, ...interna];
}

export function conceptosSoloDeclaracionViatico(empleado, boleta = 'ambas') {
  const plame = (empleado?.conceptos?.ingresos || []).filter(esConceptoDeclaracionViatico);
  const interna = (empleado?.conceptosInternos?.ingresos || []).filter(esConceptoDeclaracionViatico);
  if (boleta === 'plame') return plame;
  if (boleta === 'interna') return interna;
  return [...plame, ...interna];
}

const sumarConceptos = (arr = []) =>
  (arr || []).reduce((s, c) => s + (parseFloat(c.monto) || 0), 0);

/** Lista de tareos del empleado (vista abierta o cerrada). */
function obtenerTareosEmpleado(empleado) {
  if (!empleado) return [];
  if (Array.isArray(empleado.tareosEmpleadoFiltrados) && empleado.tareosEmpleadoFiltrados.length) {
    return empleado.tareosEmpleadoFiltrados;
  }
  if (Array.isArray(empleado.tareosEmpleado) && empleado.tareosEmpleado.length) {
    return empleado.tareosEmpleado;
  }
  if (Array.isArray(empleado.tareos?.tareosDetallados)) {
    return empleado.tareos.tareosDetallados;
  }
  if (Array.isArray(empleado.tareos)) {
    return empleado.tareos;
  }
  return [];
}

/**
 * Viáticos netamente desde tareos, separados por tipo_boleta.
 * - interna → solo Interna
 * - plame → solo PLAME
 * - (ambas se guardan como dos filas, una de cada tipo)
 * No usa conceptos de boleta calculada (evita PLAME fantasma).
 */
export function montosViaticosDesdeTareos(empleado) {
  let viaticosPlame = 0;
  let viaticosInterna = 0;
  obtenerTareosEmpleado(empleado).forEach((tareo) => {
    (tareo.conceptos || []).forEach((c) => {
      if (!esConceptoDeclaracionViatico(c)) return;
      const monto = parseFloat(c.monto) || 0;
      if (monto <= 0) return;
      const tb = String(c.tipo_boleta || '').toLowerCase().trim();
      if (tb === 'interna' || tb === 'interno') {
        viaticosInterna += monto;
      } else if (tb === 'plame') {
        viaticosPlame += monto;
      }
      // sin destino explícito: no se imputa (evita inventar PLAME)
    });
  });
  return {
    viaticosPlame,
    viaticosInterna,
  };
}

/**
 * Montos de declaración separados por boleta (evita sumar PLAME+interna dos veces).
 * Viáticos: siempre desde suma de tareos por destino.
 */
export function montosDeclaracionPorBoleta(empleado) {
  const via = montosViaticosDesdeTareos(empleado);
  return {
    gratiPlame: sumarConceptos(conceptosSoloDeclaracionGratificacion(empleado, 'plame')),
    gratiInterna: sumarConceptos(conceptosSoloDeclaracionGratificacion(empleado, 'interna')),
    viaticosPlame: via.viaticosPlame,
    viaticosInterna: via.viaticosInterna,
  };
}

/**
 * Suma montos de viáticos / grati de un empleado.
 * Viáticos: desde tareos por destino. Grati: desde conceptos de boleta.
 * @param {'ambas'|'plame'|'interna'} boleta
 */
export function totalesDeclaracionEmpleado(empleado, boleta = 'ambas') {
  const grati = conceptosSoloDeclaracionGratificacion(empleado, boleta);
  const via = montosViaticosDesdeTareos(empleado);
  let totalViaticos = 0;
  if (boleta === 'plame') totalViaticos = via.viaticosPlame;
  else if (boleta === 'interna') totalViaticos = via.viaticosInterna;
  else totalViaticos = via.viaticosPlame + via.viaticosInterna;
  return {
    totalGratificaciones: sumarConceptos(grati),
    totalViaticos,
    empleadosConGrati: grati.length > 0 ? 1 : 0,
    empleadosConViatico: totalViaticos > 0 ? 1 : 0,
  };
}

/**
 * Filas para Excel: conceptos informativos (grati / viáticos) que no entran al neto.
 * @param {object[]} empleadosCompletos - con conceptos (sin filtrar boleta ordinaria)
 * @param {'plame'|'interna'} boleta
 */
export function filasConceptosNoIngresanAlPago(empleadosCompletos = [], boleta = 'plame') {
  const filas = [];
  (empleadosCompletos || []).forEach((emp) => {
    const ingresos =
      boleta === 'interna'
        ? emp?.conceptosInternos?.ingresos || []
        : emp?.conceptos?.ingresos || [];
    (ingresos || []).forEach((c) => {
      if (!esConceptoSoloDeclaracion(c)) return;
      const monto = parseFloat(c.monto) || 0;
      if (monto <= 0) return;
      const codigo = String(c.codigoPlame || c.codigo || c.codigo_plame || '');
      const descripcion = c.descripcion || c.concepto || c.nombre || codigo || 'Sin descripción';
      const tipo = esConceptoDeclaracionViatico(c) ? 'Viáticos' : 'Gratificación';
      filas.push({
        dni: emp.documento || emp.numero_documento || '',
        empleado: emp.nombre || emp.nombre_completo || '',
        tipo,
        codigo,
        descripcion,
        monto,
      });
    });
  });
  return filas;
}

/**
 * Marca/inyecta 0406-0313 del módulo de gratificaciones como declaración PLAME
 * (no boleta). También inyecta la grati alternativa en conceptosInternos.
 * No toca liquidación ni conceptos manuales ajenos al módulo.
 */
export function aplicarDeclaracionGratificacionDesdeModulo(empleado) {
  if (!empleado) return empleado;
  const gratificaciones = Array.isArray(empleado.gratificaciones_planilla)
    ? empleado.gratificaciones_planilla
    : [];
  if (gratificaciones.length === 0) return empleado;

  const porCodigo = new Map();
  const porCodigoInterna = new Map();
  gratificaciones.forEach((grat) => {
    (grat.calculos_conceptos || []).forEach((cc) => {
      const codigo = String(cc.codigo_plame || '');
      const monto = parseFloat(cc.monto) || 0;
      if (!codigo || monto <= 0) return;
      porCodigo.set(codigo, {
        codigo,
        monto,
        descripcion: cc.descripcion || codigo,
        id: cc.id,
      });
    });
    (grat.calculos_conceptos_interna || []).forEach((cc) => {
      const codigo = String(cc.codigo_plame || '');
      const monto = parseFloat(cc.monto) || 0;
      if (!codigo || monto <= 0) return;
      porCodigoInterna.set(codigo, {
        codigo,
        monto,
        descripcion: cc.descripcion || codigo,
        id: cc.id,
      });
    });
  });

  let next = empleado;

  if (porCodigo.size > 0) {
    const ingresosActuales = [...(next.conceptos?.ingresos || [])];
    const ingresos = ingresosActuales.map((ing) => {
      if (esConceptoLiquidacionPlanilla(ing)) return ing;
      const codigo = String(ing.codigoPlame || ing.codigo || '');
      if (!porCodigo.has(codigo)) return ing;
      return {
        ...ing,
        origenCalculo: 'gratificacion',
        origen_calculo: 'gratificacion',
        excluirBoletaOrdinaria: true,
        excluir_boleta_ordinaria: true,
        origen: ing.origen || 'gratificacion_declaracion',
      };
    });

    const codigosPresentes = new Set(
      ingresos.map((ing) => String(ing.codigoPlame || ing.codigo || ''))
    );
    porCodigo.forEach((dato, codigo) => {
      if (codigosPresentes.has(codigo)) return;
      ingresos.push({
        id: dato.id || `grati_${codigo}`,
        codigo,
        descripcion: dato.descripcion,
        monto: dato.monto,
        codigoPlame: codigo,
        base_calculo: 0,
        tipo_concepto: 'ingreso',
        origen: 'gratificacion_declaracion',
        excluirBoletaOrdinaria: true,
        excluir_boleta_ordinaria: true,
        origenCalculo: 'gratificacion',
        origen_calculo: 'gratificacion',
      });
    });

    next = {
      ...next,
      conceptos: {
        ...next.conceptos,
        ingresos,
      },
    };
  }

  if (porCodigoInterna.size > 0) {
    const baseInternos = next.conceptosInternos || { ingresos: [], descuentos: [], aportes: [] };
    const ingresosInternosActuales = [...(baseInternos.ingresos || [])];
    const ingresosInternos = ingresosInternosActuales.map((ing) => {
      if (esConceptoLiquidacionPlanilla(ing)) return ing;
      const codigo = String(ing.codigoPlame || ing.codigo || '');
      if (!porCodigoInterna.has(codigo)) return ing;
      return {
        ...ing,
        origenCalculo: 'gratificacion',
        origen_calculo: 'gratificacion',
        excluirBoletaOrdinaria: true,
        excluir_boleta_ordinaria: true,
        origen: ing.origen || 'gratificacion_declaracion_interna',
      };
    });

    const codigosInternosPresentes = new Set(
      ingresosInternos.map((ing) => String(ing.codigoPlame || ing.codigo || ''))
    );
    porCodigoInterna.forEach((dato, codigo) => {
      if (codigosInternosPresentes.has(codigo)) return;
      ingresosInternos.push({
        id: dato.id || `grati_int_${codigo}`,
        codigo,
        descripcion: dato.descripcion,
        monto: dato.monto,
        codigoPlame: codigo,
        base_calculo: 0,
        tipo_concepto: 'ingreso',
        origen: 'gratificacion_declaracion_interna',
        excluirBoletaOrdinaria: true,
        excluir_boleta_ordinaria: true,
        origenCalculo: 'gratificacion',
        origen_calculo: 'gratificacion',
      });
    });

    next = {
      ...next,
      conceptosInternos: {
        ...baseInternos,
        ingresos: ingresosInternos,
      },
    };
  }

  return next;
}
